/**
 * Shared JWT access-token helpers used by BOTH the axios client and the
 * WebSocket managers, so REST and WS share a single refresh path and never
 * (re)connect with a stale / expired token.
 *
 * Why this exists: previously the axios 401 interceptor refreshed the access
 * token in localStorage, but the WebSocket clients embedded the token in the
 * connect URL and reconnected with the original (now-expired) token — so a
 * long-lived session would lose real-time notifications / inbox updates with a
 * repeating `WebSocket handshake: 403` loop until a full page reload.
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000'

/** Decode a JWT and report whether it is expired (with a small clock skew). */
export function isTokenExpired(token: string | null, skewSeconds = 30): boolean {
  if (!token) return true
  try {
    const part = token.split('.')[1]
    if (!part) return true
    // JWT payloads are base64url — normalise before decoding.
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/')
    const { exp } = JSON.parse(atob(base64))
    if (!exp) return false
    return Date.now() / 1000 >= exp - skewSeconds
  } catch {
    return true
  }
}

// Dedup concurrent refreshes (axios interceptor + multiple WS reconnects) into
// a single in-flight request so we never fire several /token/refresh calls.
let refreshPromise: Promise<string> | null = null

async function doRefresh(): Promise<string> {
  const refresh = localStorage.getItem('refresh_token')
  if (!refresh) throw new Error('No refresh token')

  const res = await fetch(`${API_BASE_URL}/api/token/refresh`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ refresh }),
  })
  if (!res.ok) throw new Error(`Token refresh failed: ${res.status}`)

  const data = (await res.json()) as { access: string }
  const access = data.access
  localStorage.setItem('access_token', access)

  // Keep the Zustand auth store in sync so hooks (e.g. useWebSocket) re-render
  // with the new token. Dynamic import avoids an import cycle (authStore ->
  // notification-ws -> auth-token -> authStore).
  import('@/stores/authStore')
    .then(({ useAuthStore }) => useAuthStore.getState().setAccessToken(access))
    .catch(() => {})

  return access
}

/**
 * Exchange the refresh token for a new access token. Concurrent callers share
 * the same in-flight request. Throws if there is no refresh token or the
 * refresh fails.
 */
export function refreshAccessToken(): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = doRefresh().finally(() => {
      refreshPromise = null
    })
  }
  return refreshPromise
}

/**
 * Return a valid (non-expired) access token, refreshing first if needed.
 * Returns `null` when no valid token can be obtained (e.g. the refresh token
 * itself expired) — callers should then STOP retrying to avoid hammering the
 * server with doomed requests.
 */
export async function getValidAccessToken(): Promise<string | null> {
  const token = localStorage.getItem('access_token')
  if (token && !isTokenExpired(token)) return token
  try {
    return await refreshAccessToken()
  } catch {
    return null
  }
}
